import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, ShoppingCart, Loader2, User, Tag, Package, Info,
  Download, Clock, AlertTriangle, CheckCircle2, RotateCcw,
} from "lucide-react";
import { toast } from "sonner";

import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AssetPricingBadge } from "@/components/marketplace/AssetPricingBadge";
import { AssetDownloadButton } from "@/components/marketplace/AssetDownloadButton";
import { AiAssetBadge } from "@/components/marketplace/AiAssetBadge";
import { useCart } from "@/context/CartContext";

/**
 * Phase 24e · Paid asset detail page.
 * Route: /marketplace/asset/:id
 */
export default function AssetDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [asset, setAsset] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [buying, setBuying] = useState(false);
  const [myTx, setMyTx] = useState(null); // buyer's transaction for this asset

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch(`/marketplace/paid/asset/${encodeURIComponent(id)}`);
      setAsset(res);
      if (res?.owned && user) {
        const tx = await apiFetch(`/marketplace/purchases?limit=100`);
        const mine = (tx?.items || []).find((t) => t.asset_id === id && t.status === "completed");
        setMyTx(mine || null);
      } else {
        setMyTx(null);
      }
    } catch (e) {
      setError(e?.message || "load_error");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function handleBuy() {
    if (!user) {
      toast.error("Devi accedere per acquistare");
      // FASE I · full reload to Next.js SSR landing (not SPA LandingV4Page).
      window.location.replace("/");
      return;
    }
    setBuying(true);
    try {
      const res = await apiFetch(`/marketplace/purchase`, {
        method: "POST",
        body: JSON.stringify({ asset_id: id }),
      });
      toast.success("Acquisto completato!");
      setMyTx(res?.transaction || null);
      await load();
    } catch (e) {
      const msg = String(e?.message || e);
      toast.error(`Acquisto fallito: ${msg}`);
    } finally {
      setBuying(false);
    }
  }

  async function handleRefund() {
    if (!myTx) return;
    if (!window.confirm("Confermi il rimborso? Solo entro 24h e se non hai ancora scaricato.")) return;
    try {
      await apiFetch(`/marketplace/purchases/${myTx.id}/refund`, {
        method: "POST",
        body: JSON.stringify({ reason: "User initiated" }),
      });
      toast.success("Rimborso richiesto");
      await load();
    } catch (e) {
      toast.error(`Rimborso fallito: ${String(e?.message || e)}`);
    }
  }

  return (
    <div className="min-h-screen bg-[var(--color-bg)] text-[var(--color-text-main)]">
      <Header />
      <div className="flex">
        <Sidebar />
        <main className="flex-1 p-6 md:p-10 max-w-5xl mx-auto w-full">
          <button
            data-testid="asset-detail-back"
            onClick={() => navigate(-1)}
            className="mb-6 inline-flex items-center gap-2 text-xs text-text-secondary hover:text-[var(--color-primary)] transition-colors"
          >
            <ArrowLeft className="h-4 w-4" /> Indietro
          </button>

          {loading && (
            <div className="flex items-center justify-center py-24">
              <Loader2 className="h-8 w-8 animate-spin text-[var(--color-primary)]" />
            </div>
          )}

          {error && (
            <div
              data-testid="asset-detail-error"
              className="rounded-2xl border border-[var(--color-alert)]/40 bg-[var(--color-alert)]/5 p-6 text-sm text-[var(--color-alert)]"
            >
              <AlertTriangle className="h-5 w-5 inline mr-2" />
              {error}
            </div>
          )}

          {asset && !loading && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="space-y-6"
              data-testid="asset-detail-page"
            >
              <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-6 md:p-8">
                <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-3 flex-wrap">
                      <AssetPricingBadge
                        is_paid={asset.is_paid}
                        price_eur_cents={asset.price_eur_cents}
                        owned={asset.owned}
                      />
                      {(asset.ai_generated || asset.creator_type === "virtual") && (
                        <AiAssetBadge assetAiType={asset.asset_ai_type} size="sm" withGlow />
                      )}
                      <span
                        data-testid="asset-category"
                        className="text-[10px] uppercase tracking-widest text-text-secondary px-2 py-0.5 rounded-full border border-white/10"
                      >
                        {asset.category || "general"}
                      </span>
                    </div>
                    <h1
                      data-testid="asset-title"
                      className="text-3xl md:text-4xl font-semibold tracking-tight mb-2"
                    >
                      {asset.title}
                    </h1>
                    <Link
                      to={`/creator/${asset.creator_id}/store`}
                      data-testid="asset-creator-link"
                      className="inline-flex items-center gap-2 text-xs text-text-secondary hover:text-[var(--color-primary)] transition-colors"
                    >
                      <User className="h-3 w-3" /> {asset.creator_id?.slice(0, 8)}…
                    </Link>
                  </div>

                  <div className="flex flex-col items-end gap-2">
                    {asset.is_own_asset ? (
                      <span
                        data-testid="own-asset-tag"
                        className="text-xs px-3 py-1 rounded-full border border-[var(--color-secondary)]/40 bg-[var(--color-secondary)]/10 text-[var(--color-secondary)]"
                      >
                        Il tuo asset
                      </span>
                    ) : asset.owned && myTx ? (
                      <div className="flex flex-col items-end gap-2">
                        <span
                          data-testid="asset-owned-tag"
                          className="text-xs px-3 py-1 rounded-full border border-[var(--color-success)]/40 bg-[var(--color-success)]/10 text-[var(--color-success)] inline-flex items-center gap-1"
                        >
                          <CheckCircle2 className="h-3 w-3" /> Già acquistato
                        </span>
                        <AssetDownloadButton
                          transactionId={myTx.id}
                          downloadCount={myTx.download_count || 0}
                          maxDownloads={myTx.max_downloads || 3}
                        />
                        <button
                          data-testid="asset-refund-btn"
                          onClick={handleRefund}
                          className="text-[10px] uppercase tracking-widest text-text-secondary hover:text-[var(--color-alert)] transition-colors inline-flex items-center gap-1"
                        >
                          <RotateCcw className="h-3 w-3" /> Rimborso (24h · 0 download)
                        </button>
                      </div>
                    ) : (
                      <div className="flex flex-col items-end gap-2">
                        <button
                          data-testid="asset-buy-btn"
                          onClick={handleBuy}
                          disabled={buying || !asset.is_paid}
                          className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold uppercase tracking-widest text-xs transition-all ${
                            buying
                              ? "bg-white/5 text-text-secondary cursor-not-allowed"
                              : "bg-white hover:bg-[#EDEDED] text-black hover:shadow-lg hover:shadow-[var(--color-primary)]/30"
                          }`}
                        >
                          {buying ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShoppingCart className="h-4 w-4" />}
                          {buying ? "In corso…" : `Acquista · $${((asset.price_eur_cents || 0) / 100).toFixed(2).replace(".", ",")}`}
                        </button>
                        <AddToCartButton assetId={id} price={asset.price_eur_cents} isPaid={asset.is_paid} />
                      </div>
                    )}
                  </div>
                </div>

                <p className="text-sm text-text-secondary leading-relaxed whitespace-pre-line">
                  {asset.description || "Nessuna descrizione disponibile."}
                </p>

                {(asset.tags || []).length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-4">
                    {asset.tags.map((t) => (
                      <span
                        key={t}
                        className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border border-white/10 text-text-secondary inline-flex items-center gap-1"
                      >
                        <Tag className="h-2.5 w-2.5" /> {t}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <MetaCard icon={Package} label="Tipo" value={asset.asset_type || "template"} />
                <MetaCard icon={Info} label="Vendite" value={String(asset.sales_count || 0)} />
                <MetaCard
                  icon={Clock}
                  label="Refund window"
                  value="24h · 0 dl"
                  hint="Rimborso possibile entro 24h e senza download"
                />
                <MetaCard
                  icon={Download}
                  label="Download limit"
                  value="3 max"
                  hint="Ogni acquisto abilita 3 download"
                />
              </div>
            </motion.div>
          )}
        </main>
      </div>
    </div>
  );
}

function MetaCard({ icon: Icon, label, value, hint }) {
  return (
    <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-4">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-text-secondary mb-1.5">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className="text-sm font-semibold text-[var(--color-text-main)]">{value}</div>
      {hint && <p className="text-[10px] text-text-secondary mt-1 leading-tight">{hint}</p>}
    </div>
  );
}

function AddToCartButton({ assetId, price, isPaid }) {
  const cart = useCart();
  const inCart = cart.has(assetId);
  if (!isPaid || !price) return null;
  return (
    <button
      data-testid="asset-add-to-cart-btn"
      disabled={inCart}
      onClick={() => cart.add(assetId)}
      className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-[10px] uppercase tracking-widest border transition-colors ${
        inCart
          ? "bg-[var(--color-success)]/10 text-[var(--color-success)] border-[var(--color-success)]/40 cursor-default"
          : "border-[var(--color-primary)]/60 text-[var(--color-primary)] hover:bg-[var(--color-primary)]/10"
      }`}
    >
      {inCart ? "✓ Added to cart" : "+ Add to cart"}
    </button>
  );
}
